
import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Printer } from 'lucide-react';
import { RegistrationFormValues } from '@/schemas/registrationSchema';
import { formatCurrency } from '@/lib/utils';
import { formatTShirtSize } from '@/utils/formatUtils';
import QRCode from 'react-qr-code';
import { formatQRCode } from '@/utils/qrCodeUtils';

interface PrintRegistrationProps {
  formData: RegistrationFormValues;
  confirmationCode: string;
}

const PrintRegistration: React.FC<PrintRegistrationProps> = ({ 
  formData, 
  confirmationCode 
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  
  // Calculate total amount for display with safety checks
  const baseAmount = 500000;
  const additionalAmount = (formData.additionalContribution && !isNaN(Number(formData.additionalContribution))) 
    ? Number(formData.additionalContribution) 
    : 0;
  const totalAmount = baseAmount + additionalAmount;
  
  const handlePrint = () => {
    const printContent = document.createElement('div');
    if (printRef.current) {
      printContent.innerHTML = printRef.current.innerHTML;
      
      // Enhanced print CSS for better QR code quality
      const style = document.createElement('style');
      style.innerHTML = `
        @media print {
          body { 
            font-family: Arial, sans-serif; 
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
          }
          .print-header { 
            font-size: 22px; 
            font-weight: bold; 
            text-align: center; 
            margin-bottom: 24px; 
          }
          .print-section { 
            margin-bottom: 18px;
            line-height: 1.5;
          }
          .print-label { 
            font-weight: bold; 
          }
          .print-value { 
            margin-left: 12px;
          }
          .print-footer { 
            margin-top: 32px; 
            font-style: italic; 
            font-size: 14px; 
            text-align: center; 
          }
          .print-qr-container { 
            display: flex; 
            justify-content: center; 
            margin: 24px 0;
            page-break-inside: avoid;
          }
          .print-tshirt { 
            color: #0070f3; 
            font-weight: bold; 
          }
          .print-code {
            text-align: center;
            font-family: monospace;
            font-size: 18px;
            font-weight: bold;
            margin: 12px 0;
            letter-spacing: 1px;
          }
          /* Improved QR code print quality */
          svg {
            height: 250px !important;
            width: 250px !important;
            border: 1px solid #ddd;
            padding: 12px;
            background-color: white;
          }
        }
      `;
      
      printContent.appendChild(style);
      
      // Save current body content
      const originalContents = document.body.innerHTML;
      
      // Replace body content with print content
      document.body.innerHTML = printContent.innerHTML;
      
      // Print
      window.print();
      
      // Restore original content
      document.body.innerHTML = originalContents;
    }
  };
  
  // Format the t-shirt size for display with safety check
  const formattedTShirtSize = formatTShirtSize(formData.t_shirt_size || 'Nam_L');
  
  // Ensure the QR code is properly formatted with LHP- prefix and exactly 6 characters
  const qrData = formatQRCode(confirmationCode);
  
  // Debug log to verify code format
  console.log(`Print QR with formatted code: ${qrData}`);
  
  return (
    <>
      <motion.div
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="mt-4"
      >
        <Button
          type="button"
          onClick={handlePrint}
          variant="outline"
          className="w-full flex items-center justify-center gap-2 border-blue-300 text-blue-600 hover:bg-blue-50"
        >
          <Printer className="h-4 w-4" />
          <span>In thông tin đăng ký</span>
        </Button>
      </motion.div>
      
      {/* Hidden content for printing */}
      <div ref={printRef} className="hidden">
        <div className="print-header">Thông tin đăng ký tham dự</div>
        
        <div className="print-section">
          <div><span className="print-label">Họ và tên:</span> <span className="print-value">{formData.fullName || ""}</span></div>
          <div><span className="print-label">Email:</span> <span className="print-value">{formData.email || ""}</span></div>
          <div><span className="print-label">Số điện thoại:</span> <span className="print-value">{formData.phone || "Chưa cung cấp"}</span></div>
          <div><span className="print-label">Lớp:</span> <span className="print-value">{formData.class || ""}</span></div>
          <div><span className="print-label">Size áo:</span> <span className="print-value print-tshirt">{formattedTShirtSize}</span></div>
          <div><span className="print-label">Đóng góp:</span> <span className="print-value">{formatCurrency(totalAmount.toString())}đ</span></div>
        </div>
        
        <div className="print-qr-container">
          <QRCode 
            value={qrData} 
            size={250}
            level="H" 
            fgColor="#000000"
            bgColor="#FFFFFF"
          />
        </div>
        
        <div className="print-code">{qrData}</div>
        
        <div className="print-footer">
          Vui lòng mang theo mã QR này khi tham dự sự kiện để check-in nhanh chóng.
        </div>
      </div>
    </>
  );
};

export default PrintRegistration;
